import React, { useState, useCallback, useEffect } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { LoadingSpinner, EmptyState } from '../components/layout/SharedComponents';
import api, { getApiErrorMessage } from '../services/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ShoppingCart, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { SortableTableHeader, useSortState } from '../components/SortableTableHeader';

function SortableHeader({ label, sortKey, currentSort, onSort, className = '' }) {
  const isActive = currentSort.key === sortKey;
  const icon = isActive ? (currentSort.dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />;
  return (
    <th className={`px-3 py-2 font-semibold select-none cursor-pointer hover:bg-accent/30 transition-colors text-xs ${className}`}
      onClick={() => onSort(sortKey)}>
      <span className="inline-flex items-center gap-1">{label} {icon}</span>
    </th>
  );
}

function sortData(data, key, dir) {
  if (!key) return data;
  return [...data].sort((a, b) => {
    let av = a[key] ?? '', bv = b[key] ?? '';
    if (typeof av === 'number' && typeof bv === 'number') return dir === 'asc' ? av - bv : bv - av;
    return dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });
}

const fmtNum = (v) => v == null ? '—' : typeof v === 'number' ? v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(v);

export default function ReplenishmentTab({ onItemClick }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: 'urgency', dir: 'desc' });
  const [selected, setSelected] = useState(new Set());
  const [batchRequesting, setBatchRequesting] = useState(false);

  const toggleSort = (key) => setSort(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/inventory/replenishment/status');
      setData(r.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleAll = (pending) => {
    setSelected(prev => {
      if (prev.size === pending.length) return new Set();
      return new Set(pending.map(n => `${n.item_code}::${n.location_code}`));
    });
  };

  const createSingle = async (items) => {
    try {
      const r = await api.post('/api/inventory/replenishment/create-order', { items });
      toast.success(`Requisition ${r.data.request_number} created with ${r.data.created?.length || 0} item(s)`, { duration: 6000 });
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
  };

  const createBatch = async () => {
    if (!data?.data) return;
    const pending = data.data.filter(n => !n.existing_order);
    const toOrder = pending
      .filter(n => selected.has(`${n.item_code}::${n.location_code}`))
      .map(n => ({
        item_code: n.item_code, location_code: n.location_code,
        quantity: n.suggested_order_qty, supplier_code: n.preferred_supplier_code,
      }));
    if (toOrder.length === 0) { toast.info('Select items to include in the batch request'); return; }
    setBatchRequesting(true);
    try {
      const r = await api.post('/api/inventory/replenishment/batch-request', { items: toOrder });
      const reqs = r.data.requisitions || [];
      if (reqs.length === 1) {
        toast.success(`Requisition ${reqs[0].request_number} created for ${reqs[0].supplier} (${reqs[0].items_count} item(s))`, { duration: 6000 });
      } else {
        toast.success(`${reqs.length} requisitions created, grouped by supplier: ${reqs.map(r => `${r.request_number} (${r.supplier})`).join(', ')}`, { duration: 10000 });
      }
      setSelected(new Set());
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Batch request failed')); }
    setBatchRequesting(false);
  };

  if (loading) return <LoadingSpinner />;
  if (!data || data.total === 0) return <EmptyState title="All stock levels OK" description="No items are currently below their minimum stock or reorder levels." />;

  const pendingItems = data.data.filter(n => !n.existing_order);
  const selectedCount = pendingItems.filter(n => selected.has(`${n.item_code}::${n.location_code}`)).length;
  const allPendingSelected = pendingItems.length > 0 && selectedCount === pendingItems.length;

  return (
    <div>
      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="border rounded-xl p-3 bg-red-50">
          <p className="text-2xl font-bold text-red-700">{data.summary?.CRITICAL || 0}</p>
          <p className="text-[10px] text-red-600 font-semibold">Critical (Out of Stock)</p>
        </div>
        <div className="border rounded-xl p-3 bg-amber-50">
          <p className="text-2xl font-bold text-amber-700">{data.summary?.HIGH || 0}</p>
          <p className="text-[10px] text-amber-600 font-semibold">High (Below 50% of Min)</p>
        </div>
        <div className="border rounded-xl p-3 bg-blue-50">
          <p className="text-2xl font-bold text-blue-700">{data.summary?.MEDIUM || 0}</p>
          <p className="text-[10px] text-blue-600 font-semibold">Medium (Below Reorder)</p>
        </div>
        <div className="border rounded-xl p-3 bg-purple-50">
          <p className="text-xl font-bold text-purple-700">{fmtNum(data.summary?.total_estimated_cost || 0)}</p>
          <p className="text-[10px] text-purple-600 font-semibold">Est. Replenishment Cost</p>
        </div>
      </div>

      {/* Replenishment items table */}
      <div className="border rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead><tr className="bg-muted/50 border-b">
            <th className="px-3 py-2 w-8 text-center">
              <input type="checkbox" className="h-3.5 w-3.5 rounded cursor-pointer accent-primary"
                data-testid="replenish-select-all"
                checked={allPendingSelected}
                onChange={() => toggleAll(pendingItems)}
                title="Select all pending items" />
            </th>
            <SortableHeader label="Urgency" sortKey="urgency" currentSort={sort} onSort={toggleSort} className="text-left" />
            <SortableHeader label="Item" sortKey="item_code" currentSort={sort} onSort={toggleSort} className="text-left" />
            <SortableHeader label="Location" sortKey="location_code" currentSort={sort} onSort={toggleSort} className="text-left" />
            <SortableHeader label="Available" sortKey="available" currentSort={sort} onSort={toggleSort} className="text-right" />
            <SortableHeader label="Min Level" sortKey="minimum_stock_level" currentSort={sort} onSort={toggleSort} className="text-right" />
            <SortableHeader label="Shortfall" sortKey="shortfall" currentSort={sort} onSort={toggleSort} className="text-right" />
            <SortableHeader label="Suggested Qty" sortKey="suggested_order_qty" currentSort={sort} onSort={toggleSort} className="text-right" />
            <th className="text-left px-3 py-2 font-semibold">Supplier</th>
            <SortableHeader label="Est. Cost" sortKey="estimated_cost" currentSort={sort} onSort={toggleSort} className="text-right" />
            <th className="text-center px-3 py-2 font-semibold w-24">Status</th>
          </tr></thead>
          <tbody>
            {sortData(data.data, sort.key, sort.dir).map((n, i) => {
              const urgColors = { CRITICAL: 'bg-red-100 text-red-700', HIGH: 'bg-amber-100 text-amber-700', MEDIUM: 'bg-blue-100 text-blue-700' };
              const rowKey = `${n.item_code}::${n.location_code}`;
              const isChecked = selected.has(rowKey);
              const isPending = !n.existing_order;
              return (
                <tr key={i} className={`border-b hover:bg-accent/20 cursor-pointer ${isChecked ? 'bg-primary/5' : ''}`}
                  onClick={(e) => { if (e.target.type === 'checkbox') return; onItemClick?.(n.item_code); }}
                  data-testid={`replenish-row-${n.item_code}`}>
                  <td className="px-3 py-2 text-center">
                    {isPending ? (
                      <input type="checkbox" className="h-3.5 w-3.5 rounded cursor-pointer accent-primary"
                        data-testid={`replenish-select-${n.item_code}`}
                        checked={isChecked}
                        onChange={() => toggleSelect(rowKey)} />
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2"><Badge className={`text-[8px] ${urgColors[n.urgency] || ''}`}>{n.urgency}</Badge></td>
                  <td className="px-3 py-2"><span className="font-semibold text-primary underline decoration-dotted underline-offset-2 cursor-pointer">{n.item_code}</span><br/><span className="text-muted-foreground">{n.description?.slice(0, 30)}</span></td>
                  <td className="px-3 py-2">{n.location_code}</td>
                  <td className="px-3 py-2 text-right font-semibold text-red-600">{fmtNum(n.available)}</td>
                  <td className="px-3 py-2 text-right">{fmtNum(n.minimum_stock_level)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{fmtNum(n.shortfall)}</td>
                  <td className="px-3 py-2 text-right font-bold text-primary">{fmtNum(n.suggested_order_qty)}</td>
                  <td className="px-3 py-2">{n.preferred_supplier_name || n.preferred_supplier_code || '—'}</td>
                  <td className="px-3 py-2 text-right">{fmtNum(n.estimated_cost)}</td>
                  <td className="px-3 py-2 text-center">
                    {n.existing_order ? (
                      <Badge className="text-[8px] bg-blue-100 text-blue-700 cursor-pointer" onClick={() => navigate(`/requests/${n.existing_order.request_number}`)}>
                        Requested ({n.existing_order.request_number})
                      </Badge>
                    ) : (
                      <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                        onClick={(e) => { e.stopPropagation(); createSingle([{
                          item_code: n.item_code, location_code: n.location_code,
                          quantity: n.suggested_order_qty, supplier_code: n.preferred_supplier_code,
                        }]); }}>
                        Request
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Batch replenishment controls */}
      <div className="flex items-center justify-between mt-4 border-t pt-3">
        <div className="text-xs text-muted-foreground">
          {selectedCount > 0 && <span className="font-semibold text-primary">{selectedCount} selected • </span>}
          {pendingItems.length} item(s) pending •{' '}
          {new Set(pendingItems.map(n => n.preferred_supplier_code || 'Unknown')).size} supplier(s)
        </div>
        <Button onClick={createBatch} disabled={batchRequesting || selectedCount === 0}
          data-testid="batch-replenish-btn">
          <ShoppingCart className="h-3.5 w-3.5 mr-1" />
          {batchRequesting ? 'Creating...' : `Batch Replenishment${selectedCount > 0 ? ` (${selectedCount} item${selectedCount > 1 ? 's' : ''})` : ''}`}
        </Button>
      </div>
    </div>
  );
}
